import type { TooltipProps as OrbitTooltipProps } from '@apollo/orbit';
import {
  Tooltip as OrbitTooltip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from '@apollo/orbit';
import type { DOMAttributes, ReactElement } from 'react';
import React, { cloneElement, useEffect, useState } from 'react';

export type TooltipProps = Omit<
  OrbitTooltipProps,
  'children' | 'isDisabled'
> & {
  disabled?: boolean;
  /**
   * Controls whether the tooltip stays open when contents are hovered over
   */
  interactive?: boolean;
  /**
   * Controls wether the tooltip messages changes when contents are clicked
   */
  clickedLabel?: React.ReactNode;
  /**
   * Renders the (interactive) tooltip's content through a React Portal,
   * detaching it from the trigger's DOM hierarchy. Use this when the
   * tooltip lives inside a container with `overflow: hidden` / `auto`
   * (e.g. a `TableContainer` or a `Card`) that would otherwise clip the
   * popover. No-op for the non-interactive variant — Orbit's
   * `OrbitTooltip` already portals by default.
   */
  usePortal?: boolean;
  children?: ReactElement<
    Partial<DOMAttributes<unknown>> & { tabIndex?: number }
  >;
  ref?: React.Ref<HTMLDivElement>;
};

type InternalTooltipProps = Omit<OrbitTooltipProps, 'children'> & TooltipProps;

/**
 * Render an interactive and themed Tooltip using Chakra.
 *
 * Chakra's tooltips are minimal and we need additional features for our uses
 * in Studio. This component extends the basic tooltip with Interactive and
 * Confirmation behavior.
 */
export const Tooltip = ({
  disabled,
  interactive,
  label,
  clickedLabel,
  usePortal,
  children,
  ...props
}: TooltipProps) => {
  const [isClicked, setIsClicked] = useState(false);
  useEffect(() => {
    if (isClicked) {
      const timeout = setTimeout(() => {
        setIsClicked(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isClicked]);

  const sharedProps = {
    closeOnClick: !clickedLabel,
    isDisabled: disabled,
    label: isClicked ? clickedLabel : label,
    ...props,
  };

  const wrappedChildren = children ? (
    clickedLabel ? (
      cloneElement(children, {
        // pass through args for onClick so the child component can handle
        // onClick however it wants, despite the wrapper
        onClick: (
          ...args: Parameters<NonNullable<typeof children.props.onClick>>
        ) => {
          setIsClicked(true);
          if (children.props.onClick) children.props.onClick(...args);
        },
      })
    ) : (
      children
    )
  ) : (
    <></>
  );

  // `usePortal` is meaningful only for the interactive variant — the
  // non-interactive OrbitTooltip already portals by default and doesn't
  // accept the prop.
  return interactive ? (
    <InteractiveTooltip {...sharedProps} usePortal={usePortal}>
      {wrappedChildren}
    </InteractiveTooltip>
  ) : (
    <OrbitTooltip {...sharedProps}>{wrappedChildren}</OrbitTooltip>
  );
};

const InteractiveTooltip = ({
  children,
  className,
  label,
  placement,
  isOpen: inputtedIsOpen,
  isDisabled = false,
  usePortal = false,
}: InternalTooltipProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure({ isOpen: inputtedIsOpen });

  const popoverContent = !isDisabled && (
    <PopoverContent
      className={className}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onFocus={onOpen}
      onBlur={onClose}
    >
      {label}
    </PopoverContent>
  );

  return (
    <Popover
      placement={placement}
      isOpen={isOpen}
      trigger="hover"
      gutter={0}
      variant="tooltip"
    >
      {children && (
        <PopoverTrigger>
          {cloneElement(children, {
            onMouseEnter: onOpen,
            onMouseLeave: onClose,
            onFocus: onOpen,
            onBlur: onClose,
            tabIndex: 0,
          })}
        </PopoverTrigger>
      )}
      {/* When the trigger lives inside a `overflow: hidden | auto`
          ancestor (e.g. a TableContainer/Card), inline-rendered popover
          content gets clipped. Portaling the content escapes that
          hierarchy. */}
      {usePortal ? <Portal>{popoverContent}</Portal> : popoverContent}
    </Popover>
  );
};
